import React from 'react';
import { Progress, Typography } from 'antd';
import type { ActiveOperationProjection } from '../../routes/execution/dashboardProjection';
import type { ExecutionCargoHydration } from '../types';
import { formatOperationalDateTime } from '../../routes/execution/operationalContextFacade';
import type { ExecutionActionSubmitPayload } from '../ExecutionActionModal';
import type { ExecutionIncidentType } from '../../routes/execution/incidentFacade';
import type { DispatchOrchestration } from './dispatchOrchestration';
import DispatchActionSurface from './DispatchActionSurface';

const { Text } = Typography;

type Props = {
  routeId: number;
  activeOperation: ActiveOperationProjection | null;
  orchestration: DispatchOrchestration;
  cargoAtStop: ExecutionCargoHydration[];
  onSubmit: (payload: ExecutionActionSubmitPayload) => Promise<void>;
  onOpenCompletion?: () => void;
  onResolveIncident?: (incidentId: number) => void;
  onModeChange?: () => void;
  actionLoading?: boolean;
  incidentReportRequest?: ExecutionIncidentType | null;
  onClearIncidentRequest?: () => void;
};

function cargoShort(lines: ActiveOperationProjection['cargoSummary']): string {
  if (lines.length === 0) return '—';
  return lines.map((c) => `${c.cargoName} · ${c.qty}`).join(', ');
}

export const ActiveOperationPanel: React.FC<Props> = ({
  routeId,
  activeOperation: op,
  orchestration,
  cargoAtStop,
  onSubmit,
  onOpenCompletion,
  onResolveIncident,
  onModeChange,
  actionLoading = false,
  incidentReportRequest = null,
  onClearIncidentRequest
}) => {
  return (
    <section className="execution-workspace__section execution-workspace__section--focus">
      <div className="execution-workspace__section-title">Текущая операция</div>
      {op ? (
        <>
          <div className="execution-workspace__op-main">
            {op.operationType === 'loading' ? 'Погрузка' : 'Выгрузка'} · {op.status}
          </div>
          <div className="execution-workspace__op-grid">
            <div>
              <div className="execution-workspace__muted">Адрес</div>
              <div>{op.address ?? '—'}</div>
            </div>
            {op.application.number ? (
              <div>
                <div className="execution-workspace__muted">Заявка</div>
                <div>№{op.application.number}</div>
              </div>
            ) : null}
            {op.application.client ? (
              <div>
                <div className="execution-workspace__muted">Клиент</div>
                <div>{op.application.client}</div>
              </div>
            ) : null}
            {op.application.consignee ? (
              <div>
                <div className="execution-workspace__muted">Грузополучатель</div>
                <div>{op.application.consignee}</div>
              </div>
            ) : null}
            <div>
              <div className="execution-workspace__muted">План / факт</div>
              <div>
                {formatOperationalDateTime(op.plannedAt)} / {formatOperationalDateTime(op.actualAt)}
              </div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <div className="execution-workspace__muted">Груз</div>
              <div>{cargoShort(op.cargoSummary)}</div>
            </div>
          </div>
          <Progress percent={op.progressPercent} size="small" showInfo={false} style={{ marginTop: 6 }} />
        </>
      ) : (
        <Text className="execution-workspace__muted">Нет активной точки — см. маршрут ниже</Text>
      )}
      <DispatchActionSurface
        routeId={routeId}
        orchestration={orchestration}
        cargoAtStop={cargoAtStop}
        onSubmit={onSubmit}
        onOpenCompletion={onOpenCompletion}
        onResolveIncident={onResolveIncident}
        onModeChange={onModeChange}
        loading={actionLoading}
        incidentReportRequest={incidentReportRequest}
        onClearIncidentRequest={onClearIncidentRequest}
      />
    </section>
  );
};

export default ActiveOperationPanel;
